import { trackedFetch } from "@/lib/server-request-activity";
import {
  streetViewCameraIsOffset,
  streetViewPovTowardProperty,
} from "@/lib/street-view-heading";
import { useEffect, useState } from "react";

export type StreetViewAim = {
  heading: number | null;
  panoId: string | null;
  cameraLatitude: number | null;
  cameraLongitude: number | null;
};

const headingCache = new Map<string, StreetViewAim | null>();
const inFlightHeadingRequests = new Map<
  string,
  Promise<StreetViewAim | null>
>();

const emptyAim: StreetViewAim = {
  heading: null,
  panoId: null,
  cameraLatitude: null,
  cameraLongitude: null,
};

/**
 * Heading from the Street View panorama that the Static API will use,
 * toward the property coordinates.
 *
 * The metadata request uses the same location the image is chosen from
 * (address when we have one, otherwise the property coordinates). Heading
 * is the bearing from that camera to the property, not a default of 0.
 */
export function useStreetViewHeading(
  address: string | null,
  fallbackLatitude: number | null,
  fallbackLongitude: number | null,
  enabled = true,
): StreetViewAim & { isLoading: boolean } {
  const cacheKey =
    fallbackLatitude !== null && fallbackLongitude !== null
      ? `coords:${fallbackLatitude.toFixed(6)},${fallbackLongitude.toFixed(6)}|${address?.trim().toLowerCase() ?? ""}`
      : address
        ? `addr:${address.trim().toLowerCase()}`
        : null;

  const cached =
    cacheKey != null && headingCache.has(cacheKey)
      ? (headingCache.get(cacheKey) ?? null)
      : undefined;
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const [aimState, setAimState] = useState<{
    key: string;
    value: StreetViewAim | null;
  } | null>(
    cacheKey != null && cached !== undefined
      ? { key: cacheKey, value: cached }
      : null,
  );
  const [isLoading, setIsLoading] = useState(cached === undefined);
  const aim = aimState?.key === cacheKey ? aimState.value : null;

  useEffect(() => {
    const rememberAim = (value: StreetViewAim | null) => {
      if (!cacheKey) {
        setAimState(null);
        return;
      }
      setAimState({ key: cacheKey, value });
    };

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!enabled || !cacheKey) {
      rememberAim(null);
      setIsLoading(false);
      return;
    }

    if (!apiKey) {
      rememberAim(null);
      setIsLoading(false);
      return;
    }

    if (headingCache.has(cacheKey)) {
      rememberAim(headingCache.get(cacheKey) ?? null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    rememberAim(null);

    const fetchHeading = async (): Promise<StreetViewAim | null> => {
      let houseLat: number | null = null;
      let houseLng: number | null = null;

      if (fallbackLatitude !== null && fallbackLongitude !== null) {
        houseLat = fallbackLatitude;
        houseLng = fallbackLongitude;
      } else if (address) {
        try {
          const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${apiKey}`;
          const geocodeResponse = await trackedFetch(geocodeUrl);
          const geocodeData = await geocodeResponse.json();
          if (
            geocodeData.status === "OK" &&
            geocodeData.results &&
            geocodeData.results.length > 0
          ) {
            const location = geocodeData.results[0].geometry.location;
            houseLat = location.lat;
            houseLng = location.lng;
          }
        } catch (error) {
          console.warn("Failed to geocode address:", error);
        }
      }

      if (houseLat === null || houseLng === null) {
        return null;
      }

      const metadataTarget = address?.trim()
        ? address.trim()
        : `${houseLat},${houseLng}`;

      try {
        const metadataUrl = `https://maps.googleapis.com/maps/api/streetview/metadata?location=${encodeURIComponent(metadataTarget)}&key=${apiKey}`;
        const metadataResponse = await trackedFetch(metadataUrl);
        const metadataData = await metadataResponse.json();
        const panoLat = Number(metadataData.location?.lat);
        const panoLng = Number(metadataData.location?.lng);
        if (
          metadataData.status !== "OK" ||
          !Number.isFinite(panoLat) ||
          !Number.isFinite(panoLng)
        ) {
          return null;
        }

        const panoId =
          typeof metadataData.pano_id === "string"
            ? metadataData.pano_id
            : null;
        const offset = streetViewCameraIsOffset(
          panoLat,
          panoLng,
          houseLat,
          houseLng,
        );
        return {
          heading: offset
            ? streetViewPovTowardProperty(panoLat, panoLng, houseLat, houseLng)
                .heading
            : null,
          panoId,
          cameraLatitude: panoLat,
          cameraLongitude: panoLng,
        };
      } catch (error) {
        console.warn("Failed to get Street View metadata:", error);
        return null;
      }
    };

    const existingPromise = inFlightHeadingRequests.get(cacheKey);
    const headingPromise =
      existingPromise ??
      fetchHeading()
        .then((calculated) => {
          headingCache.set(cacheKey, calculated);
          return calculated;
        })
        .finally(() => {
          inFlightHeadingRequests.delete(cacheKey);
        });

    if (!existingPromise) {
      inFlightHeadingRequests.set(cacheKey, headingPromise);
    }

    void headingPromise.then((resolved) => {
      if (cancelled) return;
      rememberAim(resolved);
      setIsLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [address, fallbackLatitude, fallbackLongitude, cacheKey, enabled]);

  const resolvedAim =
    !enabled || !cacheKey ? null : cached !== undefined ? cached : aim;
  const resolvedLoading = Boolean(
    enabled &&
      cacheKey &&
      googleMapsApiKey &&
      cached === undefined &&
      (isLoading || resolvedAim == null),
  );

  return {
    ...(resolvedAim ?? emptyAim),
    isLoading: resolvedLoading,
  };
}
